import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, ChevronRight, Search, Calendar, RefreshCw, FilterX, Filter,
  Tag, Users, X, TrendingUp, TrendingDown, Download
} from 'lucide-react';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
  PieChart, Pie, Cell
} from 'recharts';
import './DashboardBultos.css';
import SellOutDashboardMenuHeader from '../components/SellOutDashboardMenuHeader';
import { useQuery } from '../QueryContext';
import { clearCache } from '../utils/dashboardCache';
import { useAuth } from '../AuthContext';
import * as XLSX from 'xlsx';
import FullScreenLoader from './FullScreenLoader';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

// --- HELPERS ---
const formatNumber = (num) => new Intl.NumberFormat('de-DE').format(Math.round(num || 0));
const formatK = (num) => (num >= 1000 || num <= -1000) ? `${(num / 1000).toFixed(1)}K` : Math.round(num || 0);

const _process_client_brand_table_data = (flatData) => {
  if (!Array.isArray(flatData)) return [];
  
  const clientMap = {};

  flatData.forEach(row => {
    const cName = row.client_name || 'SIN CLIENTE';
    const bName = row.brand_name || 'SIN MARCA';
    const sName = row.sku_name || 'SIN PRODUCTO';
    const so = Number(row.sellout) || 0;
    const si = Number(row.sellin) || 0;

    if (!clientMap[cName]) {
      clientMap[cName] = { name: cName, sellout: 0, sellin: 0, brandsMap: {} };
    }
    clientMap[cName].sellout += so;
    clientMap[cName].sellin += si;

    if (!clientMap[cName].brandsMap[bName]) {
      clientMap[cName].brandsMap[bName] = { name: bName, sellout: 0, sellin: 0, skusMap: {} };
    }
    clientMap[cName].brandsMap[bName].sellout += so;
    clientMap[cName].brandsMap[bName].sellin += si;

    if (!clientMap[cName].brandsMap[bName].skusMap[sName]) {
      clientMap[cName].brandsMap[bName].skusMap[sName] = { name: sName, so: 0, si: 0, efectividad: 0 };
    }
    clientMap[cName].brandsMap[bName].skusMap[sName].so += so;
    clientMap[cName].brandsMap[bName].skusMap[sName].si += si;
  });

  return Object.values(clientMap).map(client => {
    const brands = Object.values(client.brandsMap).map(brand => {
      const skus = Object.values(brand.skusMap).map(sku => ({
        ...sku,
        efectividad: sku.si > 0 ? (sku.so / sku.si) * 100 : 0
      })).sort((a, b) => b.so - a.so);

      return {
        name: brand.name,
        sellout: brand.sellout,
        sellin: brand.sellin,
        efectividad: brand.sellin > 0 ? (brand.sellout / brand.sellin) * 100 : 0,
        skus: skus
      };
    }).sort((a, b) => b.sellout - a.sellout);

    return {
      name: client.name,
      sellout: client.sellout,
      sellin: client.sellin,
      efectividad: client.sellin > 0 ? (client.sellout / client.sellin) * 100 : 0,
      brands: brands
    };
  }).sort((a, b) => b.sellout - a.sellout);
};

const COLORS = ['#1a2c4e', '#0068b3', '#00ba52', '#4f8cff', '#00e6b8', '#334767'];

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.03) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 12;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} fill="#cbd5e1" fontSize="10" fontWeight="600" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const filterLabels = {
  SM_SUCURSAL: "Sucursal",
  SM_CLIENTE_PRIN: "Cliente Principal",
  SM_CANAL_MAESTRA: "Canal",
  SM_REGION: "Región",
  SM_VENDEDOR: "Vendedor",
  SM_PROVEEDOR: "Proveedor",
  SM_MARCA: "Marca",
  SM_LINEA: "Línea",
  SM_CLASIF2: "Clasificación",
  SM_SEGMENTO: "Segmento",
  SM_SKU_NOMBRE: "Producto"
};

const nombresMeses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const INITIAL_FILTERS = {
  SM_SUCURSAL: [], SM_CLIENTE_PRIN: [], SM_CANAL_MAESTRA: [], SM_REGION: [],
  SM_VENDEDOR: [], SM_PROVEEDOR: [], SM_MARCA: [],
  SM_LINEA: [], SM_CLASIF2: [], SM_SEGMENTO: [], SM_SKU_NOMBRE: []
};

// --- COMPONENTE: GlobalSearch ---
const GlobalSearch = ({ availableOptions, filterLabels, filters, setFilters }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);

  const allOptions = useMemo(() => {
    return Object.entries(availableOptions || {}).flatMap(([key, options]) =>
      (options || []).map(value => ({
        key,
        label: filterLabels[key] || key,
        value: String(value).trim()
      }))
    );
  }, [availableOptions, filterLabels]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim().length > 1) {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = allOptions
          .filter(opt => opt.value.toLowerCase().includes(lowerSearch))
          .slice(0, 20);
        setSuggestions(filtered);
        setShowDropdown(true);
        setSelectedIndex(-1);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, allOptions]);

  const handleSelect = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: (prev[key] || []).includes(value) ? prev[key] : [...(prev[key] || []), value]
    }));
    setSearchTerm('');
    setShowDropdown(false);
  }, [setFilters]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      if (selectedIndex >= 0) {
        handleSelect(suggestions[selectedIndex].key, suggestions[selectedIndex].value);
      } else if (suggestions.length > 0) {
        handleSelect(suggestions[0].key, suggestions[0].value);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setSearchTerm('');
    }
  };

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase()
        ? <mark key={i} style={{ background: '#ffeb3b', color: '#000', padding: '0 2px', borderRadius: '2px' }}>{part}</mark>
        : part
    );
  };

  return (
    <div className="global-search-container" ref={dropdownRef}>
      <div className="global-search-input-wrapper">
        <Search size={16} className="global-search-icon" />
        <input
          type="text"
          className="global-search-input"
          placeholder="Buscar cliente, marca..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {searchTerm && <X size={14} className="clear-search" style={{ cursor: 'pointer', opacity: 0.5, marginRight: '8px' }} onClick={() => setSearchTerm('')} />}
      </div>

      <AnimatePresence>
        {showDropdown && suggestions.length > 0 && (
          <motion.div className="global-search-dropdown" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <div className="suggestions-list" transition={isMobile ? { duration: 0.1 } : { duration: 0.2 }}>
              {suggestions.map((opt, i) => (
                <div
                  key={`${opt.key}-${i}`}
                  className={`suggestion-item ${i === selectedIndex ? 'selected' : ''}`}
                  onClick={() => handleSelect(opt.key, opt.value)}
                >
                  <span className="suggestion-category">{opt.label}:</span>
                  <span className="suggestion-value">
                    {highlightMatch(opt.value, searchTerm)}
                  </span>
                  {(filters[opt.key] || []).includes(opt.value) && <span className="suggestion-active" style={{ color: '#00ba52', marginLeft: 'auto' }}>✓</span>}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- COMPONENTE: MultiSelectFilter ---
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = (options || []).filter(opt => String(opt).toLowerCase().includes(searchTerm.toLowerCase())).slice(0, 100);
  const areAllSelected = filteredOptions.length > 0 && filteredOptions.every(opt => (selectedValues || []).includes(opt));

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = (selectedValues || []).filter(val => !filteredOptions.includes(val));
      onChange(name, newSelected);
    } else {
      const newSelected = [...new Set([...(selectedValues || []), ...filteredOptions])];
      onChange(name, newSelected);
    }
  };

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button className={`filter-pill-button ${(selectedValues || []).length > 0 ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{(selectedValues || []).length === 0 ? 'Todos' : (selectedValues.length === 1 ? selectedValues[0] : `${selectedValues.length} Sel.`)}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={isMobile ? { duration: 0.1 } : { duration: 0.2 }} className="filter-dropdown-panel with-search">
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
            </div>
            {filteredOptions.length > 0 && (
              <label className="checkbox-row select-all-row">
                <input type="checkbox" checked={areAllSelected} onChange={handleSelectAll} />
                <span>Seleccionar Todos</span>
              </label>
            )}
            <div className="options-list">
              {filteredOptions.map((opt, i) => (
                <label key={i} className="checkbox-row">
                  <input type="checkbox" checked={(selectedValues || []).includes(opt)} onChange={() => {
                    const currentVals = selectedValues || [];
                    const newVals = currentVals.includes(opt) ? currentVals.filter(v => v !== opt) : [...currentVals, opt];
                    onChange(name, newVals);
                  }} />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
            {(selectedValues || []).length > 0 && <button className="reset-pill-link" onClick={() => onChange(name, [])}>Limpiar</button>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- COMPONENTE PRINCIPAL ---
const SellOutMod = () => {
  const { somodQuery, getSomodFilters } = useQuery();
  const [isLocalLoading, setIsLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [unitMode, setUnitMode] = useState('bultos');
  const [metrics, setMetrics] = useState({ sellout: 0, sellin: 0, efectividad: 0, var_year: 0, var_month: 0, var_efec_year: 0 });
  const [chartData, setChartData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [expandedClients, setExpandedClients] = useState({});
  const [breakdownType] = useState('VENDEDOR');
  const [monthlyTableMeses] = useState(() => {
    const d = new Date();
    return nombresMeses.slice(0, d.getMonth() + 1);
  });
  const [monthlyAdvisorData, setMonthlyAdvisorData] = useState([]);
  const [expandedBrands, setExpandedBrands] = useState({});

  const [availableOptions, setAvailableOptions] = useState({});
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [selectedMeses, setSelectedMeses] = useState([nombresMeses[new Date().getMonth()]]);
  const [selectedAnios, setSelectedAnios] = useState([new Date().getFullYear()]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const { session } = useAuth();
  const filtersInitialized = useRef(false);

  const metricsQueryToken = useRef(0);
  const advisorQueryToken = useRef(0);

  useEffect(() => {
    const loadFilters = async () => {
      setIsLocalLoading(true);
      try {
        const config = await getSomodFilters();
        if (!config) return;

        const baseOptions = config.availableOptions || {};
        setAvailableOptions(baseOptions);

        if (config.availableYears && config.availableYears.length > 0) {
          setSelectedAnios([Number(config.currentYear) || config.availableYears[0]]);
        } else {
          setSelectedAnios([new Date().getFullYear()]);
        }
        if (config.availableMeses && config.availableMeses.length > 0) {
          const currentMonthName = nombresMeses[new Date().getMonth()].toUpperCase();
          const defaultMonth = config.availableMeses.find(m => m.toUpperCase() === currentMonthName)
            || config.availableMeses[config.availableMeses.length - 1];
          setSelectedMeses([nombresMeses.find(m => m.toUpperCase() === defaultMonth.toUpperCase()) || defaultMonth]);
        } else {
          setSelectedMeses([nombresMeses[new Date().getMonth()]]);
        }

        filtersInitialized.current = true;
      } catch (err) {
        console.error('Error loading SOMod config:', err);
      } finally {
        setIsLocalLoading(false);
      }
    };
    loadFilters();
  }, [getSomodFilters]);

  const calculateMetrics = useCallback(async (snapshotFilters, snapshotMeses, snapshotAnios, snapshotMode) => {
    if (!filtersInitialized.current) return;


    
    const thisToken = ++metricsQueryToken.current;
    setQuerying(true);
    setPieData([]);
    setTableData([]);
    setChartData([]);
    
    try {
      const safeMeses = Array.isArray(snapshotMeses) ? [...snapshotMeses] : [snapshotMeses];
      const safeAnios = Array.isArray(snapshotAnios) ? [...snapshotAnios] : [snapshotAnios];
      
      const mesesMayusculas = safeMeses.map(m => String(m).toUpperCase());
      const todosLosMesesMayusculas = nombresMeses.map(m => m.toUpperCase());

      const summaryResponse = await somodQuery({
        report_type: 'summary',
        metric_type: snapshotMode === 'unidades' ? 'UNIDADES' : 'BULTOS',
        page_meses: mesesMayusculas,
        page_anios: safeAnios, 
        filters: snapshotFilters,
        session: session,
        format: 'parquet',
        _nocache: `sum-${safeAnios.join('-')}-${snapshotMode}-${Date.now()}`
      });

      if (thisToken !== metricsQueryToken.current) return;

      if (summaryResponse) {
        if (summaryResponse.kpis && summaryResponse.kpis.length > 0) {
          const k = summaryResponse.kpis[0]; 
          setMetrics({
            sellout: Number(k.sellout) || 0,
            sellin: Number(k.sellin) || 0,
            efectividad: Number(k.efectividad) || 0,
            var_year: Number(k.var_year) || 0,
            var_month: Number(k.var_month) || 0,
            var_efec_year: Number(k.var_efec_year) || 0
          });
        }
        setPieData(summaryResponse?.pie || []);
        if (summaryResponse.table_som && Array.isArray(summaryResponse.table_som)) {
          if (summaryResponse.table_som.length > 0 && summaryResponse.table_som[0]?.client_name) {
             const dataJerarquica = _process_client_brand_table_data(summaryResponse.table_som);
             setTableData(dataJerarquica);
          } else {
             setTableData(summaryResponse.table_som);
          }
        }
      }

      const trendResponse = await somodQuery({
        report_type: 'summary',
        metric_type: snapshotMode === 'unidades' ? 'UNIDADES' : 'BULTOS',
        page_meses: todosLosMesesMayusculas,
        page_anios: safeAnios,
        filters: snapshotFilters,
        session: session,
        format: 'parquet',
        _nocache: `trend-${safeAnios.join('-')}-${snapshotMode}-${Date.now()}`
      });

      if (thisToken !== metricsQueryToken.current) return;

      if (trendResponse && trendResponse.trend) {
        setChartData(trendResponse.trend);
      }

    } catch (err) {
      console.error("Error fetching SOMod metrics via Parquet:", err);
    } finally {
      if (thisToken === metricsQueryToken.current) {
        setQuerying(false);
      }
    }
  }, [somodQuery, session]);

  const calculateMonthlyAdvisorMetrics = useCallback(async (snapshotFilters, snapshotAnios, snapshotMode) => {
    if (!filtersInitialized.current) return;
    
    const thisToken = ++advisorQueryToken.current;
    
    try {
      const safeAnios = Array.isArray(snapshotAnios) ? [...snapshotAnios] : [snapshotAnios];
      const mesesTablaMayusculas = monthlyTableMeses.map(m => m.toUpperCase());

      const response = await somodQuery({
        report_type: 'monthly_advisor',
        metric_type: snapshotMode === 'unidades' ? 'UNIDADES' : 'BULTOS',
        breakdown_type: breakdownType,
        page_meses: mesesTablaMayusculas,
        page_anios: safeAnios,
        filters: snapshotFilters,
        session: session,
        _nocache: `adv-${safeAnios.join('-')}-${snapshotMode}-${Date.now()}`
      });

      if (thisToken !== advisorQueryToken.current) return;

      setMonthlyAdvisorData(response?.data ?? []);
    } catch (err) {
      console.error("Error calculating monthly metrics:", err);
      if (thisToken === advisorQueryToken.current) {
        setMonthlyAdvisorData([]);
      }
    }
  }, [breakdownType, monthlyTableMeses, somodQuery, session]);

  const stringifiedFilters = JSON.stringify(filters);
  const stringifiedMeses = JSON.stringify(selectedMeses);
  const stringifiedAnios = JSON.stringify(selectedAnios);

  useEffect(() => {
    calculateMetrics(filters, selectedMeses, selectedAnios, unitMode);
  }, [stringifiedFilters, stringifiedMeses, stringifiedAnios, unitMode, calculateMetrics, filters, selectedMeses, selectedAnios]);

  useEffect(() => {
    calculateMonthlyAdvisorMetrics(filters, selectedAnios, unitMode);
  }, [stringifiedFilters, stringifiedAnios, breakdownType, unitMode, calculateMonthlyAdvisorMetrics, filters, selectedAnios]);

  // ⚡ GENERACIÓN DE LLAVE REACTIVA GLOBAL:
  // Si cambia un mes, año o el modo de unidades, esta variable cambia de string y destruye cualquier vestigio antiguo en el DOM.
  const globalRenderKey = `${stringifiedMeses}-${stringifiedAnios}-${unitMode}-${stringifiedFilters}`;

  const handleFilterChange = (name, values) => setFilters(prev => ({ ...prev, [name]: values }));
  const resetFilters = () => setFilters(INITIAL_FILTERS);

  const handleRefresh = async () => {
    clearCache();
    await Promise.all([
      calculateMetrics(filters, selectedMeses, selectedAnios, unitMode),
      calculateMonthlyAdvisorMetrics(filters, selectedAnios, unitMode)
    ]);
  };

  const handleExportExcel = (type = 'standard') => {
    if (tableData.length === 0) return;

    if (type === 'monthly') {
      const rows = [];
      const entityLabel = breakdownType;
      const sortedM = [...monthlyTableMeses].sort((a, b) => nombresMeses.indexOf(a) - nombresMeses.indexOf(b));

      monthlyAdvisorData.forEach(adv => {
        const row = { [entityLabel]: adv.entidad };
        sortedM.forEach(mesName => {
          const m = adv.months[nombresMeses.indexOf(mesName)] || { venta: 0, cuota: 0, logro: 0 };
          row[`${mesName}_SellIn`] = Math.round(m.cuota);
          row[`${mesName}_SellOut`] = Math.round(m.venta);
          row[`${mesName}_Efec`] = Math.round(m.logro) + '%';
        });
        row.Total_SellOut = Math.round(adv.totalReal);
        rows.push(row);
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Mensual");
      XLSX.writeFile(wb, `SellOut_Moderno_Mensual_${new Date().getTime()}.xlsx`);
      return;
    }

    const rows = [];
    tableData.forEach(client => {
      client.brands.forEach(brand => {
        brand.skus.forEach(sku => {
          rows.push({
            Cliente: client.name,
            Marca: brand.name,
            Producto: sku.name,
            Sell_In: Math.round(sku.si || 0),
            Sell_Out: Math.round(sku.so || 0),
            Efectividad_Pct: Math.round(sku.efectividad || 0)
          });
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SellOut");
    XLSX.writeFile(wb, `SellOut_Moderno_${new Date().getTime()}.xlsx`);
  };

  const CustomChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ backgroundColor: 'white', padding: '10px', borderRadius: '5px', boxShadow: '0 2px 10px rgba(0,0,0,0.2)', border: '1px solid #ccc' }}>
          <p style={{ color: '#333', fontWeight: 'bold', marginBottom: '5px' }}>{label}</p>
          <p style={{ color: '#0068b3', fontSize: '0.8rem', margin: 0 }}>Sell Out: {formatNumber(payload[0].value)}</p>
          <p style={{ color: '#00ba52', fontSize: '0.8rem', margin: 0 }}>Sell In: {formatNumber(payload[1].value)}</p>
        </div>
      );
    }
    return null;
  };

  if (isLocalLoading && !filtersInitialized.current) return <FullScreenLoader message="Consultando API de Sell-Out Moderno..." />;


  return (
    <motion.div className="dashboard-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <SellOutDashboardMenuHeader />

      <header className="header-flex">
        <div className="title-section">
          <h1 className="dashboard-title">Sell Out Moderno</h1>
          <div className="title-underline"></div>
        </div>
        <div className="date-selectors">
          <button className="refresh-button" onClick={handleRefresh} disabled={querying} title="Actualizar Datos">
            <RefreshCw size={16} className={querying ? 'spin-anim' : ''} />
          </button>
          <button className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)}>
            <Filter size={16} />
            <span>Filtros</span>
          </button>
          <div className="switch-toggle-group">
            <button className={`switch-toggle-btn ${unitMode === 'bultos' ? 'active' : ''}`} onClick={() => setUnitMode('bultos')}>Bultos</button>
            <button className={`switch-toggle-btn ${unitMode === 'unidades' ? 'active' : ''}`} onClick={() => setUnitMode('unidades')}>Unidades</button>
          </div>
          <div className="date-filters-group">
            <MultiSelectFilter 
            label="Mes" 
            name="mes" 
            icon={Calendar} 
            options={nombresMeses} 
            selectedValues={selectedMeses} 
            onChange={(name, v) => {
              const order = new Map(nombresMeses.map((m, idx) => [m, idx]));
              const sorted = [...(v || [])].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
              setSelectedMeses(sorted);
            }}
          />
          <MultiSelectFilter 
            label="Año" 
            name="anio" 
            icon={Calendar} 
            options={isMobile ? [new Date().getFullYear()] : [2024, 2025, 2026]} 
            selectedValues={selectedAnios} 
            onChange={(name, v) => setSelectedAnios(v)} // Corrección de firma idéntica
          />
        </div>
          <div style={isMobile ? { width: '100%', marginTop: '8px' } : {}}>
            <GlobalSearch
              availableOptions={availableOptions}
              filterLabels={filterLabels}
              filters={filters}
              setFilters={setFilters}
            />
          </div>
        </div>
      </header>

      <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
        <div className="filters-sidebar-header">
          <h3>Filtros Disponibles</h3>
          <div className="filters-header-actions">
            <button className="reset-filters-btn" onClick={resetFilters} title="Restablecer filtros">
              <FilterX size={16} />
            </button>
            <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="filters-grid">
          {Object.keys(filters).map((key) => (
            <MultiSelectFilter key={key} label={filterLabels[key] || key} name={key} icon={key.includes('VENDEDOR') || key.includes('CLIENTE') ? Users : Tag} options={availableOptions[key]} selectedValues={filters[key]} onChange={handleFilterChange} />
          ))}
          <button className="filter-pill-button reset-filters" onClick={resetFilters}>
            <FilterX size={12} className="pill-icon" />
            <span className="pill-label">Limpiar</span>
          </button>
        </div>
      </div>
      {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

      <div className="dashboard-indicators">
        <div className="indicator-card v-real">
          <div className="card-content">
            <span className="indicator-label">Total Sell Out</span>
            <div className="indicator-row">
              <span className="indicator-value">{formatNumber(metrics.sellout)}</span>
              <div className="card-variations">
                <span className={`variation-item ${metrics.var_year >= 0 ? 'positive' : 'negative'}`}>
                  {metrics.var_year >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {Math.abs(metrics.var_year || 0).toFixed(1)}% Anual
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="indicator-card q-meta">
          <div className="card-content">
            <span className="indicator-label">Total Sell In</span>
            <div className="indicator-row">
              <span className="indicator-value">{formatNumber(metrics.sellin)}</span>
              <div className="card-variations">
                <span className={`variation-item ${metrics.var_month >= 0 ? 'positive' : 'negative'}`}>
                  {metrics.var_month >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {Math.abs(metrics.var_month || 0).toFixed(1)}% Mensual
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="indicator-card p-cumplimiento">
          <div className="card-content">
            <span className="indicator-label">% Efectividad SO/SI</span>
            <div className="indicator-row">
              <span className="indicator-value">{(metrics.efectividad || 0).toFixed(1)}%</span>
              <div className="card-variations">
                <span className={`variation-item ${metrics.var_efec_year >= 0 ? 'positive' : 'negative'}`}>
                  {metrics.var_efec_year >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  {Math.abs(metrics.var_efec_year || 0).toFixed(1)}% Anual
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="charts-grid main-row" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s' }}>
        <div className="chart-container">
          <h3 className="chart-title">Comparativo Sell In vs Sell Out ({selectedAnios[0]})</h3>
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart
                key={`trend-${globalRenderKey}`}
                data={chartData}
                margin={{ top: 20, right: 10, left: -10, bottom: 0 }}
              >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff20" animationDuration={isMobile ? 0 : 400} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#fff' }} />
              <YAxis axisLine={false} tickLine={false} tickFormatter={formatK} tick={{ fontSize: 9, fill: '#fff' }} />
              <Tooltip content={<CustomChartTooltip />} />
              <Legend verticalAlign="bottom" height={16} iconType="circle" wrapperStyle={{ fontSize: '10px', backgroundColor: 'rgba(255, 255, 255, 0.85)', borderRadius: '12px', padding: '5px', width: '90%', margin: '0 auto', left: '5%', bottom: 0, display: 'flex', justifyContent: 'center', color: '#fff' }} />
              <Bar dataKey="SellOut" fill="#0068b3" radius={[2, 2, 0, 0]} barSize={15}>
                <LabelList dataKey="SellOut" position="top" formatter={formatK} style={{ fontSize: '9px', fontWeight: 'bold', fill: '#fff' }} />
              </Bar>
              <Bar dataKey="SellIn" fill="#00ba52" radius={[2, 2, 0, 0]} barSize={15}>
                <LabelList dataKey="SellIn" position="top" formatter={formatK} style={{ fontSize: '9px', fontWeight: 'bold', fill: '#fff' }} />
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3 className="chart-title">Participación Marcas Sell Out (%)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart
              key={`pie-${globalRenderKey}`}
              animationDuration={isMobile ? 0 : 500}
            >
              <Pie
                data={pieData}
                cx="50%" cy="45%" innerRadius={60} outerRadius={85} paddingAngle={4} dataKey="value"
                labelLine={false} label={renderCustomizedLabel} animationDuration={500} animationEasing="ease-out"
              >
                {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#fff" strokeWidth={0} />)}
              </Pie>
              <Tooltip formatter={(value) => formatNumber(value)} />
              <Legend verticalAlign="bottom" height={16} iconType="circle" wrapperStyle={{ fontSize: '7px', backgroundColor: 'rgba(255, 255, 255, 0.85)', borderRadius: '12px', padding: '5px', width: '90%', margin: '0 auto', left: '5%', bottom: 0, display: 'flex', justifyContent: 'center', color: '#fff' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="charts-grid bottom-row" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s', marginTop: '20px' }}>
        <div className="chart-container mini table-fixed-height" style={{ gridColumn: 'span 2', height: '450px' }}>
          <div className="table-header-container">
            <h3 className="chart-title">Desempeño Detallado por Cliente Principal, Marca y Producto</h3>
            <button className="export-table-btn" onClick={handleExportExcel}>
              <Download size={14} /> Exportar
            </button>
          </div>
          <div className="minimal-table-wrapper">
            <div className="table-header" style={{ gridTemplateColumns: '2fr 1fr 1fr 0.8fr' }}>
              <div className="col-name">Estructura Detalle</div>
              <div className="col-data">Sell In</div>
              <div className="col-data">Sell Out</div>
              <div className="col-data">% Efec.</div>
            </div>
            <div className="table-body">
              {tableData?.map((client, cIdx) => {
                const isCliExpanded = expandedClients[client.name];
                return (
                  <React.Fragment key={cIdx}>
                    <div className="table-row advisor-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 0.8fr' }} onClick={() => setExpandedClients(prev => ({ ...prev, [client.name]: !isCliExpanded }))}>
                      <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {isCliExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        {client.name}
                      </div>
                      <div className="col-data">{formatNumber(client.si)}</div>
                      <div className="col-data">{formatNumber(client.so)}</div>
                      <div className="col-data bold">{(client.efectividad || 0).toFixed(1)}%</div>
                    </div>
                    {isCliExpanded && client.brands?.map((brand, bIdx) => {
                      const brandKey = `${client.name}-${brand.name}`;
                      const isBrandExpanded = expandedBrands[brandKey];
                      return (
                        <React.Fragment key={bIdx}>
                          <div className="table-row client-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 0.8fr', paddingLeft: '20px' }} onClick={() => setExpandedBrands(prev => ({ ...prev, [brandKey]: !isBrandExpanded }))}>
                            <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              {isBrandExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              {brand.name}
                            </div>
                            <div className="col-data">{formatNumber(brand.si)}</div>
                            <div className="col-data">{formatNumber(brand.so)}</div>
                            <div className="col-data">{(brand.efectividad || 0).toFixed(1)}%</div>
                          </div>
                          {isBrandExpanded && brand.skus?.map((sku, sIdx) => (
                            <div className="table-row marca-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 0.8fr', paddingLeft: '40px', fontSize: '0.7rem', opacity: 0.8 }} key={sIdx}>
                              <div className="col-name truncate">{sku.name}</div>
                              <div className="col-data">{formatNumber(sku.si)}</div>
                              <div className="col-data">{formatNumber(sku.so)}</div>
                              <div className="col-data">{(sku.efectividad || 0).toFixed(1)}%</div>
                            </div>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="table-footer" style={{ gridTemplateColumns: '2fr 1fr 1fr 0.8fr' }}>
              <div className="col-name">TOTALES</div>
              <div className="col-data">{formatNumber(metrics.sellin)}</div>
              <div className="col-data">{formatNumber(metrics.sellout)}</div>
              <div className="col-data bold">{(metrics.efectividad || 0).toFixed(1)}%</div>
            </div>
          </div>
        </div>
      </div>

      <footer className="status-footer">
        {querying && <span className="updating-text">Actualizando datos en tiempo real...</span>}
      </footer>
    </motion.div>
  );
};

export default SellOutMod;